/**
 *
 * SearchBar
 *
 */

import React, { useEffect, useReducer, useRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import CancelIcon from '@material-ui/icons/Cancel';
import algolia from 'utils/algoliaService';
import { useSelector } from 'react-redux';
import MixPanel from 'utils/mixpanelService';
import _classNames from 'classnames';
import { getResultsVisibility } from 'containers/Assessment/utils';
import {
  makeSelectProfile,
  makeSelectProfileFilters,
} from 'containers/Auth/selectors';
import { parseProfileForClientResources } from 'containers/Auth/utils';
import {
  makeSelectAudienceTagsRelations,
  makeSelectClientDetails,
  makeSelectSearchConfigInOtherLang,
} from 'containers/Main/selectors';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _partition from 'lodash/partition';
import IconButton from '@material-ui/core/IconButton';
import EmbedSearchIcon from 'components/BackHeader/images/search.svg';
import {
  getClientAudienceFilters,
  filterClientExclusiveResources,
} from 'containers/Resources/utils';
import useAlgoliaLocale from 'components/useAlgoliaLocale';
import useSiteCopySelector from 'components/useSiteCopySelector';
import Button from '@material-ui/core/Button';
import { DEFAULT_PRESENTATION } from 'utils/constants';
import useSiteConfigSelector from 'components/useSiteConfigSelector';
import { filterBasedOnRequiredTags } from 'utils/analyzeClientResource';
import {
  filterItemsByClientAccess,
  getClientArticleSettings,
} from 'utils/clientSettings';
import { getTranslatedQuery } from 'utils/translationService';
import Overlay from './components/Overlay';
import OverlayOld from './components/OverlayOld';
import SearchIcon from './images/white-search.svg';
import CloseIcon from './images/close-search.svg';
import {
  buildSearchAlgoliaQueries,
  getFiltersString,
  addInternalTyping,
  sortSearchPreviewResources,
  pickTopResources,
} from './utils';
import useQueryParams from './hooks/useQueryParams';

const useStyles = makeStyles(theme => ({
  searchBox: {
    position: 'relative',
    background: '#ffffff',
    color: '#000',
    height: ({ mobile }) => (mobile ? 35 : 45),
    borderRadius: 3,
    border: `1px solid #E3E3E3`,
  },
  flatBox: {
    border: 'none',
    borderBottom: '1px solid #E3E3E3',
    borderRadius: 0,
    marginBottom: 0,
  },
  cancelIcon: {
    position: 'absolute',
    height: '100%',
    right: 62,
    top: 0,
    color: '#D6D6D6',
    width: 16,
    borderRadius: 0,
    cursor: 'pointer',
    '& svg': {
      scale: '0.66',
    },
    '& path': {
      pointerEvents: 'none',
    },
  },
  iconContainer: {
    position: 'absolute',
    height: '100%',
    right: 0,
    top: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    background: theme.palette.primary.main,
    width: ({ mobile }) => (mobile ? 43 : 54),
    borderTopRightRadius: 3,
    borderBottomRightRadius: 3,
  },
  searchBtn: {
    maxHeight: ({ mobile }) => (mobile ? 35 : undefined),
    minWidth: ({ mobile }) => (mobile ? 43 : 54),
    borderTopRightRadius: 3,
    borderBottomRightRadius: 3,
  },
  searchIcon: {
    height: ({ mobile }) => (mobile ? 24 : undefined),
    width: ({ mobile }) => (mobile ? 24 : undefined),
  },
  progress: {
    height: '20px !important',
    width: '20px !important',
    color: 'white',
  },
  input: {
    flex: '1',
    width: '100%',
    height: '100%',
    paddingLeft: 16,
    paddingRight: 82,
    fontWeight: 300,
    fontSize: ({ mobile }) => (mobile ? '14px !important' : '16px !important'),
    cursor: 'text',
    border: 'none',
    borderRadius: 4,
    '-webkit-appearance': 'none',
    fontFamily: 'MadaRegular',
    color: '#4B4B4B',
    backgroundColor: 'transparent',
  },
  embedSearch: {
    fontSize: 16,
    background: 'transparent',
    borderBottom: 'none',
    height: 36,
    '& img': {
      marginLeft: 10,
      height: 16,
      width: 19,
    },
    '&:hover': {
      background: 'none',
    },
    '& input': {
      color: '#848484',
      fontSize: '14px !important',
      minWidth: 0,
      paddingRight: 20,
    },
  },
  embedIconContainer: {
    background: 'transparent',
    width: 35,
    ' & button:hover': {
      background: 'none',
    },
  },
  embedInput: {
    paddingRight: 37,
    paddingLeft: 0,
    minWidth: 200,
  },
}));

const fetchSuggestions = async ({
  query,
  filters,
  clientDetails,
  excludeResourceTypes,
  profile,
  profileFilters,
  locale = 'en-US',
  searchConfig,
}) => {
  if (query) {
    const finalQuery =
      locale !== 'en-US' ? await getTranslatedQuery(query, locale) : query;
    try {
      const numWords = query.slice(' ').length;
      const minProximity = numWords < 4 ? 1 : 3;

      const optionalFilters = [];
      if (!_isEmpty(clientDetails)) {
        optionalFilters.push(`clients:${clientDetails.shortName}<score=2>`);
        if (!_isEmpty(_get(clientDetails, 'clientGroup'))) {
          optionalFilters.push(
            `clients:${_get(clientDetails, 'clientGroup.shortName')}<score=1>`,
          );
        }
      }

      const {
        tags,
        requiredTagsOptions,
        insuranceTags,
        insuranceOptions,
      } = profileFilters;
      const requiredTagsOptionsIds = requiredTagsOptions.reduce(
        (accumulator, tag) => accumulator.concat(tag.tags),
        [],
      );
      const insuranceOptionsIds = insuranceOptions.reduce(
        (accumulator, tag) => accumulator.concat(tag.tags),
        [],
      );
      const requiredTags = insuranceTags
        .filter(tag => insuranceOptionsIds.includes(tag.id))
        .concat(tags.filter(tag => requiredTagsOptionsIds.includes(tag.id)));
      const requiredTagsFilters = _isEmpty(requiredTags)
        ? ' AND requiredTags:"none"'
        : ` AND (requiredTags:"none" OR ${requiredTags
            .map(tag => `requiredTags:"${tag.id}"`)
            .join(' OR ')})`;

      const excludedTagsFilters = _isEmpty(profileFilters.tags)
        ? ''
        : ` AND ${profileFilters.tags
            .map(tag => `NOT excludedTags:"${tag.id}"`)
            .join(' AND ')}`;

      let stateFilters = '';
      if (
        !_isEmpty(_get(profileFilters, 'state')) &&
        profileFilters.state !== 'all'
      ) {
        stateFilters = ` AND (state:"all" OR state:"${profileFilters.state}")`;
      }

      const excludedTopics = _get(
        clientDetails,
        'excludeTopicCollection.items',
        [],
      ).map(topic => topic.sys.id);
      const clientAudienceQuery = getClientAudienceFilters(
        profileFilters,
        'audience',
      );

      const { alwaysShowInformationalLink } = getClientArticleSettings(
        clientDetails,
      );
      const queries = buildSearchAlgoliaQueries({
        query: finalQuery,
        filters,
        optionalFilters,
        excludeResourceTypes,
        excludedTopics,
        audienceFilters: clientAudienceQuery,
        minProximity,
        requiredTagsFilters,
        excludedTagsFilters,
        stateFilters,
        alwaysShowInformationalLink,
      });

      const content = await algolia.multipleQueries(queries);

      const originalQueryCount = queries.length / 2;

      const getResultByIndex = index => {
        const exactResult = content.results[index];
        if (exactResult.nbHits > 0) {
          return exactResult;
        }
        return content.results[index + originalQueryCount];
      };

      const topicsResult = getResultByIndex(0);
      const assessmentsResult = getResultByIndex(1);
      const blogsResult = getResultByIndex(2);
      const remainingResult = getResultByIndex(3);
      const seriesResult = !excludeResourceTypes.includes('Series')
        ? getResultByIndex(4)
        : null;

      const topics = excludeResourceTypes.includes('Topics')
        ? []
        : topicsResult.hits;
      const assessments = excludeResourceTypes.includes('Assessments')
        ? []
        : filterBasedOnRequiredTags({
            items: filterClientExclusiveResources({
              resources: assessmentsResult.hits,
              clientShortName: clientDetails.shortName,
              clientGroupShortName: _get(
                clientDetails,
                'clientGroup.shortName',
              ),
            }),
            insuranceTags,
            insuranceOptions,
            profileTags: tags,
            requiredTagsOptions,
            isSearchPreview: true,
          });
      const blogs = excludeResourceTypes.includes('Insights')
        ? []
        : blogsResult.hits;
      const series = seriesResult
        ? filterItemsByClientAccess({
            items: seriesResult.hits,
            clientDetails,
            paths: {
              includedClientsPath: 'clients',
              excludedClientsPath: 'clientsExclude',
              itemClientShortNamePath: '',
              itemClientGroupShortNamePath: '',
            },
          })
        : [];
      const remainingResources = filterBasedOnRequiredTags({
        items: remainingResult.hits,
        insuranceTags,
        insuranceOptions,
        profileTags: tags,
        requiredTagsOptions,
        isSearchPreview: true,
      }).filter(item => {
        if (_get(item, 'assessmentResultsVisibility.user-resources')) {
          const parsedProfile = parseProfileForClientResources(profile);
          return getResultsVisibility(
            _get(item, 'assessmentResultsVisibility.user-resources'),
            parsedProfile,
          );
        }
        return true;
      });
      const courses = remainingResources.filter(
        item => item.type === 'Courses',
      );
      const practices = remainingResources.filter(
        item => item.type === 'Practices',
      );

      const otherResources = remainingResources.filter(
        item => item.type !== 'Courses' && item.type !== 'Practices',
      );

      const [clientRemainingResources, publicRemainingResources] = _partition(
        otherResources,
        item => _get(item, 'clients', []).includes(clientDetails.shortName),
      );

      const allResources = [
        ...courses.map(r => addInternalTyping(r, 'courses')),
        ...practices.map(r => addInternalTyping(r, 'practices')),
        ...series.map(r => addInternalTyping(r, 'series')),
        ...blogs.map(r => addInternalTyping(r, 'blogs')),
        ...publicRemainingResources.map(r =>
          addInternalTyping(r, 'publicResources'),
        ),
      ];

      const resources = pickTopResources(
        allResources,
        finalQuery,
        clientDetails,
        searchConfig?.pageCopy?.resourcePriority,
      );
      const searchResourcesPreviewOrder = _get(
        clientDetails,
        'metadata.searchResourcesPreviewOrder',
        {},
      );

      const sortedResourceArray = sortSearchPreviewResources(
        resources,
        searchResourcesPreviewOrder,
      );

      const crisisFlag = !_isEmpty(
        topicsResult?.userData?.find(
          element => Object.keys(element)?.[0] === 'crisisFlag',
        ),
      );

      const suggestions = {
        topics,
        assessments,
        exclusive: clientRemainingResources.map(resource =>
          addInternalTyping(resource, 'clientResources'),
        ),
        resources: sortedResourceArray,
        crisisFlag,
      };

      return suggestions;
    } catch (err) {
      return {};
    }
  }
  return {};
};

const INITIAL_STATE = {
  processing: false,
  query: '',
  data: {},
  focused: false,
  offsetTop: 0,
};

const ON_QUERY_CHANGE = 'on-query-change';
const ON_CHANGE = 'on-change';
const ON_ENTER_PRESSED = 'on-enter-pressed';
const ON_FETCH_END = 'on-fetch-end';
const ON_CLICK_INSIDE = 'on-click-inside';
const ON_CLICK_AWAY = 'on-click-away';
const RESET = 'reset';

const reducer = (state, action) => {
  switch (action.type) {
    case ON_CHANGE:
      return {
        ...state,
        focused: true,
        processing: true,
        query: action.payload,
      };
    case ON_ENTER_PRESSED:
      return {
        ...state,
        data: {},
        focused: false,
        processing: false,
      };
    case ON_FETCH_END:
      return {
        ...state,
        data: action.payload,
        processing: false,
      };
    case ON_CLICK_INSIDE:
      return {
        ...state,
        focused: true,
        offsetTop: action.payload,
      };
    case ON_CLICK_AWAY:
      return {
        ...state,
        focused: false,
      };
    case ON_QUERY_CHANGE:
      return {
        ...state,
        query: action.payload,
      };

    case RESET:
      return INITIAL_STATE;
    default:
      throw new Error();
  }
};

const SearchBar = ({
  close,
  isFlat,
  isEmbedded,
  isVariantE,
  callback,
  variantELocale,
  otherLanguage,
  mobile = false,
  id,
  section,
}) => {
  const history = useHistory();
  const querySearch = useQueryParams();
  const classes = useStyles({ mobile });
  const locale = useAlgoliaLocale();
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const searchButtonRef = useRef(null);
  const clearRef = useRef(null);
  const timeout = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [state, dispatch] = useReducer(reducer, {
    ...INITIAL_STATE,
    query: querySearch.get('query') || '',
  });
  const [siteConfig] = useSiteConfigSelector(['Features']);

  const profile = useSelector(makeSelectProfile());
  const profileFilters = useSelector(makeSelectProfileFilters());
  const clientDetails = useSelector(makeSelectClientDetails());
  const audienceTagsRelations = useSelector(makeSelectAudienceTagsRelations());
  const excludeResourceTypes =
    _get(clientDetails, 'excludeResourceTypes') || [];
  const filters = useMemo(() => {
    const filtersString = getFiltersString(clientDetails, siteConfig, locale);
    return filtersString;
  }, [clientDetails, locale]);

  const { data, focused, processing, query, offsetTop } = state;

  const [searchConfig] = useSiteCopySelector(['search']);

  const searchConfigInOtherLang = useSelector(
    makeSelectSearchConfigInOtherLang(),
  );

  const withTrackingData = eventData => ({
    ...(eventData || {}),
    ...(section ? { section } : {}),
  });

  const onSuggestionPillClick = text => {
    MixPanel.track(
      'User Search - Click Search Tag',
      withTrackingData({
        tag: text,
      }),
    );
    onChange(text);
  };

  const onResourceClick = clickData => {
    MixPanel.track(
      'User Search - Select Result',
      withTrackingData({
        searchTerm: query,
        ...clickData,
      }),
    );
    dispatch({ type: RESET });
  };

  const onLinkClick = linkType => {
    MixPanel.track(
      'User Search - Click Link',
      withTrackingData({
        searchTerm: query,
        type: linkType,
      }),
    );
    dispatch({ type: RESET });
  };

  const onFocus = event => {
    if (!anchorEl) setAnchorEl(event.currentTarget);
    let ot = 0;
    if (containerRef) {
      const viewportRect = containerRef.current.getBoundingClientRect();
      ot = viewportRect.top;
    }
    if (callback) callback(true);
    dispatch({ type: ON_CLICK_INSIDE, payload: ot });
    MixPanel.track('User Search - Focused', withTrackingData());
  };

  const onChange = e => {
    const queryValue = typeof e === 'string' ? e : _get(e, 'target.value', '');
    dispatch({ type: ON_CHANGE, payload: queryValue });

    clearTimeout(timeout.current);

    timeout.current = setTimeout(async () => {
      if (typeof e !== 'string')
        MixPanel.track(
          'User Search - Query',
          withTrackingData({
            searchTerm: queryValue,
          }),
        );

      const suggestions = await fetchSuggestions({
        query: queryValue,
        filters,
        clientDetails,
        excludeResourceTypes,
        profile,
        profileFilters,
        locale: variantELocale || locale,
        audienceTagsRelations,
        searchConfig,
      });
      dispatch({ type: ON_FETCH_END, payload: suggestions });
    }, 300);
  };

  const onKeyDown = e => {
    if (query !== '' && e.key === 'Enter') {
      MixPanel.track(
        'User Search - Enter Pressed',
        withTrackingData({
          searchTerm: query,
        }),
      );
      if (e.target) e.target.blur();
      dispatch({ type: ON_ENTER_PRESSED });
      if (isFlat) {
        close();
      }
      if (variantELocale) {
        history.push(
          `/resources?query=${query}&presentation=${DEFAULT_PRESENTATION}&lang=${variantELocale}`,
        );
      } else {
        history.push(
          `/resources?query=${query}&presentation=${DEFAULT_PRESENTATION}`,
        );
      }
    }
  };

  const onClickAway = e => {
    const targetId = e?.target?.id;
    const isInputOrClear = targetId === id || targetId === 'search-clear';
    if (!isInputOrClear) {
      if (callback) callback(false);
      dispatch({ type: ON_CLICK_AWAY });
    }
  };

  const onClearClicked = () => {
    onChange('');

    MixPanel.track(
      'User Search - Clear Clicked',
      withTrackingData({
        searchTerm: query,
      }),
    );

    if (inputRef.current) inputRef.current.focus();
  };

  const onSearchClick = () => {
    MixPanel.track(
      'User Search - Search Clicked',
      withTrackingData({
        searchTerm: query,
      }),
    );
    if (query === '') {
      if (inputRef.current) inputRef.current.focus();
    } else {
      if (inputRef.current) inputRef.current.blur();
      dispatch({ type: ON_ENTER_PRESSED });
      if (variantELocale) {
        history.push(
          `/resources?query=${query}&presentation=${DEFAULT_PRESENTATION}&lang=${variantELocale}`,
        );
      } else {
        history.push(
          `/resources?query=${query}&presentation=${DEFAULT_PRESENTATION}`,
        );
      }
    }
  };

  const closeSearch = () => {
    close();
  };
  const showOverlay = isVariantE ? focused && !_isEmpty(data) : focused;

  useEffect(() => {
    const queryValue = querySearch.get('query');
    if (queryValue) dispatch({ type: ON_QUERY_CHANGE, payload: queryValue });
  }, [querySearch]);

  const isClientActive = _get(clientDetails, 'activeClient') ?? true;
  if (!isClientActive) return null;

  const SearchOverlay =
    isVariantE || section === 'not found' ? OverlayOld : Overlay;

  return (
    <div
      className={_classNames(classes.searchBox, {
        [classes.embedSearch]: isEmbedded,
        [classes.flatBox]: isFlat || isEmbedded,
      })}
      ref={containerRef}
    >
      <input
        aria-label="Search Resources"
        placeholder={
          isEmbedded
            ? 'CredibleMind Search'
            : _get(
                otherLanguage ? searchConfigInOtherLang : searchConfig,
                'callToAction',
                'Search topics, resources and more...',
              )
        }
        value={query}
        onChange={onChange}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        ref={inputRef}
        id={id}
        className={_classNames(classes.input, isEmbedded && classes.embedInput)}
        autoComplete="off"
      />
      {showOverlay && (
        <SearchOverlay
          anchorEl={anchorEl}
          data={data}
          focused={focused}
          offsetTop={offsetTop}
          onChange={onChange}
          section={section}
          onClickAway={onClickAway}
          onLinkClick={onLinkClick}
          onResourceClick={onResourceClick}
          onSuggestionPillClick={onSuggestionPillClick}
          query={query}
          searchConfig={otherLanguage ? searchConfigInOtherLang : searchConfig}
          isEmbedded={isEmbedded}
          isVariantE={isVariantE}
          variantELocale={variantELocale}
          focusableElements={[inputRef, clearRef, searchButtonRef]}
          otherLanguage={otherLanguage}
        />
      )}
      {query !== '' && focused && (
        <IconButton
          onClick={onClearClicked}
          ref={clearRef}
          disableRipple
          className={classes.cancelIcon}
          aria-label="Clear"
          id="search-clear"
        >
          <CancelIcon style={{ pointerEvents: 'none' }} />
        </IconButton>
      )}
      <div
        className={_classNames(
          classes.iconContainer,
          isEmbedded && classes.embedIconContainer,
        )}
      >
        {processing ? (
          <CircularProgress className={classes.progress} />
        ) : (
          <>
            {isFlat || isEmbedded ? (
              <IconButton
                onClick={closeSearch}
                disableRipple
                aria-label="Close"
              >
                <img
                  src={isEmbedded ? EmbedSearchIcon : CloseIcon}
                  alt=""
                  className={classes.close}
                />
              </IconButton>
            ) : (
              <Button
                color="primary"
                variant="contained"
                disableElevation
                onClick={onSearchClick}
                ref={searchButtonRef}
                aria-label="Search"
                tabIndex={0}
                className={classes.searchBtn}
              >
                <img src={SearchIcon} alt="" className={classes.searchIcon} />
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

SearchBar.propTypes = {
  callback: PropTypes.func,
  close: PropTypes.func,
  isEmbedded: PropTypes.bool,
  isFlat: PropTypes.bool,
  isVariantE: PropTypes.bool,
  otherLanguage: PropTypes.bool,
  variantELocale: PropTypes.string,
  mobile: PropTypes.bool,
  id: PropTypes.string,
};

SearchBar.defaultProps = {
  isVariantE: false,
  id: 'search-input',
};

export default SearchBar;
